import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Navbar, Footer } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Trophy, Video, Image, Music, Sparkles, Gift, Lock, Unlock, Phone, Mail, Upload, Play, ArrowRight, CheckCircle } from "lucide-react";
import API from "@/utils/api";

const Competitions = () => {
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompetition, setSelectedCompetition] = useState(null);
  const [entryForm, setEntryForm] = useState({
    participant_name: "",
    phone: "",
    email: "",
    submission_title: "",
    media_data: null,
    media_type: "video"
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    fetchCompetitions();
  }, []);

  const fetchCompetitions = async () => {
    try {
      const response = await axios.get(`${API}/competitions`);
      setCompetitions(response.data);
    } catch (error) {
      console.error("Error fetching competitions:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCompetitionIcon = (type) => {
    switch (type) {
      case "reels": return <Video className="w-6 h-6" />;
      case "natural_beauty": return <Sparkles className="w-6 h-6" />;
      case "creative": return <Image className="w-6 h-6" />;
      case "singing": return <Music className="w-6 h-6" />;
      case "duna100": return <Gift className="w-6 h-6" />;
      case "rasong": return <Music className="w-6 h-6" />;
      default: return <Trophy className="w-6 h-6" />;
    }
  };

  const getCompetitionColor = (type) => {
    switch (type) {
      case "reels": return "from-pink-500/10 to-rose-500/10 border-pink-200";
      case "natural_beauty": return "from-emerald-500/10 to-teal-500/10 border-emerald-200";
      case "creative": return "from-violet-500/10 to-purple-500/10 border-violet-200";
      case "singing": return "from-amber-500/10 to-orange-500/10 border-amber-200";
      case "duna100": return "from-teal-500/10 to-cyan-500/10 border-teal-200";
      case "rasong": return "from-rose-500/10 to-red-500/10 border-rose-200";
      default: return "from-slate-500/10 to-gray-500/10 border-slate-200";
    }
  };

  const getIconColor = (type) => {
    switch (type) {
      case "reels": return "text-pink-600 bg-pink-100";
      case "natural_beauty": return "text-emerald-600 bg-emerald-100";
      case "creative": return "text-violet-600 bg-violet-100";
      case "singing": return "text-amber-600 bg-amber-100";
      case "duna100": return "text-teal-600 bg-teal-100";
      case "rasong": return "text-rose-600 bg-rose-100";
      default: return "text-slate-600 bg-slate-100";
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEntryForm({
          ...entryForm,
          media_data: reader.result,
          media_type: file.type.startsWith("video") ? "video" : "image"
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmitEntry = async (e) => {
    e.preventDefault();
    if (!selectedCompetition) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/competition-entries`, {
        competition_id: selectedCompetition.id,
        participant_name: entryForm.participant_name,
        phone: entryForm.phone,
        email: entryForm.email || null,
        submission_title: entryForm.submission_title,
        media_data: entryForm.media_data,
        media_type: entryForm.media_type
      });
      setSubmitSuccess(true);
      setEntryForm({
        participant_name: "",
        phone: "",
        email: "",
        submission_title: "",
        media_data: null,
        media_type: "video"
      });
    } catch (error) {
      console.error("Error submitting entry:", error);
      alert(error.response?.data?.detail || "Failed to submit entry. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const competitionTypes = [
    { type: "reels", name: "Reels Competition", desc: "Share 30-60 second video reels", allows: ["video"] },
    { type: "natural_beauty", name: "Beauty of the Hills", desc: "Photos & videos of natural beauty", allows: ["video", "image"] },
    { type: "singing", name: "Singing Competition", desc: "Record your singing talent", allows: ["video"] },
    { type: "creative", name: "Creative Art Competition", desc: "Artwork, crafts & creative works", allows: ["video", "image"] },
  ];

  return (
    <div className="min-h-screen bg-transparent" data-testid="competitions-page">
      <Navbar />
      <div className="pt-24 pb-16 max-w-7xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full mb-4">
            <Trophy className="w-5 h-5" />
            <span className="font-medium">Showcase Your Talent</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 mb-4">Competitions</h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Participate in exciting competitions! Register with your phone number and showcase your talents.
          </p>
        </div>

        {/* Duna 100 Featured Card */}
        <Link to="/duna100">
          <Card className="mb-8 rounded-2xl overflow-hidden bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:shadow-xl transition-shadow cursor-pointer" data-testid="duna100-featured-card">
            <CardContent className="p-6 sm:p-8 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/20 rounded-full flex items-center justify-center">
                  <Gift className="w-7 h-7 sm:w-8 sm:h-8" />
                </div>
                <div>
                  <h3 className="text-xl sm:text-2xl font-bold">Duna 100</h3>
                  <p className="text-white/80 text-sm sm:text-base">Lucky draw for members - Win exciting prizes!</p>
                </div>
              </div>
              <ArrowRight className="w-6 h-6 hidden sm:block" />
            </CardContent>
          </Card>
        </Link>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-6">
            {competitionTypes.map((compType) => {
              const competition = competitions.find(c => c.competition_type === compType.type);
              const isActive = competition?.is_active || false;
              
              return (
                <Card 
                  key={compType.type} 
                  className={`rounded-3xl overflow-hidden border-2 transition-all duration-300 ${
                    isActive ? 'hover:shadow-xl cursor-pointer' : 'opacity-75'
                  } bg-gradient-to-br ${getCompetitionColor(compType.type)}`}
                  data-testid={`competition-${compType.type}`}
                >
                  <CardHeader className="p-6">
                    <div className="flex items-start justify-between">
                      <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${getIconColor(compType.type)}`}>
                        {getCompetitionIcon(compType.type)}
                      </div>
                      <Badge className={isActive ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}>
                        {isActive ? <Unlock className="w-3 h-3 mr-1" /> : <Lock className="w-3 h-3 mr-1" />}
                        {isActive ? "Active" : "Coming Soon"}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl mt-4">{compType.name}</CardTitle>
                    <CardDescription className="text-slate-600">
                      {competition?.description || compType.desc}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="px-6 pb-2">
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <span>Accepts:</span>
                      {compType.allows.includes("video") && (
                        <Badge variant="outline" className="text-xs">
                          <Video className="w-3 h-3 mr-1" /> Video
                        </Badge>
                      )}
                      {compType.allows.includes("image") && (
                        <Badge variant="outline" className="text-xs">
                          <Image className="w-3 h-3 mr-1" /> Image
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                  <CardFooter className="p-6 pt-4">
                    {isActive ? (
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            className="w-full rounded-full"
                            onClick={() => {
                              setSelectedCompetition(competition);
                              setSubmitSuccess(false);
                            }}
                            data-testid={`participate-${compType.type}-btn`}
                          >
                            <Play className="w-4 h-4 mr-2" />
                            Participate Now
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-md">
                          {submitSuccess ? (
                            <div className="text-center py-8">
                              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-green-600" />
                              </div>
                              <DialogTitle className="text-xl mb-2">Entry Submitted!</DialogTitle>
                              <DialogDescription>
                                Thank you for participating! Your entry has been recorded successfully.
                              </DialogDescription>
                              <Button 
                                className="mt-6" 
                                onClick={() => setSubmitSuccess(false)}
                              >
                                Submit Another Entry
                              </Button>
                            </div>
                          ) : (
                            <>
                              <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                  {getCompetitionIcon(compType.type)}
                                  {compType.name}
                                </DialogTitle>
                                <DialogDescription>
                                  Fill in your details and upload your submission
                                </DialogDescription>
                              </DialogHeader>
                              <form onSubmit={handleSubmitEntry} className="space-y-4 mt-4">
                                <div className="space-y-2">
                                  <Label htmlFor="name">Full Name <span className="text-red-500">*</span></Label>
                                  <Input
                                    id="name"
                                    placeholder="Enter your full name"
                                    value={entryForm.participant_name}
                                    onChange={(e) => setEntryForm({...entryForm, participant_name: e.target.value})}
                                    required
                                    data-testid="entry-name-input"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="entry-phone">
                                    <Phone className="w-4 h-4 inline mr-1" />
                                    Phone Number <span className="text-red-500">*</span>
                                  </Label>
                                  <Input
                                    id="entry-phone"
                                    type="tel"
                                    placeholder="Enter your phone number"
                                    value={entryForm.phone}
                                    onChange={(e) => setEntryForm({...entryForm, phone: e.target.value})}
                                    required
                                    data-testid="entry-phone-input"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="entry-email" className="flex items-center gap-2">
                                    <Mail className="w-4 h-4" />
                                    Email <span className="text-xs text-slate-400">(Optional)</span>
                                  </Label>
                                  <Input
                                    id="entry-email"
                                    type="email"
                                    placeholder="your@email.com (if you have one)"
                                    value={entryForm.email}
                                    onChange={(e) => setEntryForm({...entryForm, email: e.target.value})}
                                    data-testid="entry-email-input"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="title">Submission Title</Label>
                                  <Input
                                    id="title"
                                    placeholder="Give your submission a title"
                                    value={entryForm.submission_title}
                                    onChange={(e) => setEntryForm({...entryForm, submission_title: e.target.value})}
                                    data-testid="entry-title-input"
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>
                                    <Upload className="w-4 h-4 inline mr-1" />
                                    Upload {compType.allows.join(" / ")}
                                  </Label>
                                  <Input
                                    type="file"
                                    accept={compType.allows.map(t => t === "video" ? "video/*" : "image/*").join(",")}
                                    onChange={handleFileChange}
                                    className="cursor-pointer"
                                    data-testid="entry-file-input"
                                  />
                                  {entryForm.media_data && (
                                    <p className="text-xs text-green-600">File selected: {entryForm.media_type}</p>
                                  )}
                                </div>
                                <DialogFooter className="mt-6">
                                  <Button
                                    type="submit"
                                    className="w-full rounded-full"
                                    disabled={submitting}
                                    data-testid="submit-entry-btn"
                                  >
                                    {submitting ? "Submitting..." : "Submit Entry"}
                                  </Button>
                                </DialogFooter>
                              </form>
                            </>
                          )}
                        </DialogContent>
                      </Dialog>
                    ) : (
                      <Button className="w-full rounded-full" variant="outline" disabled>
                        <Lock className="w-4 h-4 mr-2" />
                        Registration Opens Soon
                      </Button>
                    )}
                  </CardFooter>
                </Card>
              );
            })}
          </div>
        )}

        <div className="mt-12 text-center">
          <Card className="inline-block rounded-2xl p-6 bg-slate-50">
            <p className="text-slate-600">
              <Lock className="w-4 h-4 inline mr-2" />
              Competitions are activated by admin. Check back regularly for active competitions!
            </p>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Competitions;
